import * as BABYLON from 'babylonjs';
import pako from 'pako';
import DepthMapWorker from './DepthMapWorker';
import { getCurrentWorkerAuthContext, handleWorkerAuthRefreshMessage } from '../workers/workerAuthHost';
import { apiRequest } from '@shared/api/apiClient';

class DepthMap {

    public static OCCLUSION_TOLERANCE: number = .1;
    private _scene: BABYLON.Scene;
    private _imageHeight: number;
    private _imageWidth: number;
    private _scaleFactor: number;
    private _imagePosition: BABYLON.Vector3;
    private _depthMapUrl: string;
    private _onDepthMapReady: any;

    private _depthMap: Uint32Array;  

    private _abortController: AbortController;
        
    private get scene() { return this._scene; }

    constructor(scene: BABYLON.Scene, depthMapUrl: string, position: IVector3D, width: number, height: number, scaleFactor: number, imageDirection: IVector3D, onDepthReady? : any) {
        this._scene = scene;
        this._imagePosition = new BABYLON.Vector3(position.X, position.Y, position.Z);
        this._imageHeight = height;
        this._imageWidth = width;
        this._scaleFactor = scaleFactor;
        this._depthMapUrl = depthMapUrl;
        this._onDepthMapReady = onDepthReady;
    }

    public dispose() {
        this._depthMap = null;
    }

    public async loadDepthMap() {
        try {
            this._abortController = new AbortController();
            var that = this;
            this.downloadAndDecompressDepthMap(this._depthMapUrl, this._imageWidth, this._imageHeight, this._abortController.signal)
            .then((depthMap) => {
                
                that._depthMap = depthMap as any;
                that._onDepthMapReady();
            })
            .catch((error) => {
                console.error('Error:', error);
            });

            //this._depthMap = await this.downloadAndDecompressDepthMap(this._depthMapUrl, this._abortController.signal);
        } finally {
            this._abortController = null;
        }
    }

    public async abortLoad() {
        if (this._abortController) {
            this._abortController.abort();
        }
    }

    public convertPositionToSphereCoordinates(position: BABYLON.Vector3) {
        var cam = this.scene.activeCamera as any;
        if (!cam) {
            throw new Error("Active camera not set in the scene");
        }

        var dir = position.subtract(this._imagePosition).add(cam.position);
        var distance = dir.length();

        var m = new BABYLON.Matrix();
        var rotQuat = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), cam?._imageRotation);
        rotQuat.toRotationMatrix(m);
        dir = dir.normalize();
        var rotatedDir = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(dir.x, dir.y, dir.z), m);

        return rotatedDir.scale(500);
    }

    public convertPositionToSphereCoordinates5(position: BABYLON.Vector3) {
        var cam = this.scene.activeCamera as any;
        if (!cam) {
            throw new Error("Active camera not set in the scene");
        }

        var dir = position.subtract(this._imagePosition);
        var distance = dir.length();

        var m = new BABYLON.Matrix();
        var rotQuat = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), cam?._imageRotation);
        rotQuat.toRotationMatrix(m);
        dir = dir.normalize();
        var rotatedDir = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(dir.x, dir.y, dir.z), m);

        var sphereCoords = this.cartesianToSpherical(new BABYLON.Vector3(rotatedDir.x, rotatedDir.z, rotatedDir.y));
        var u = 1 - sphereCoords.x;
        var v = sphereCoords.y;
        var x = u * this._imageWidth;
        var y = v * this._imageHeight;
        x = Math.floor(x);
        y = Math.floor(y);

        // Calculate screen coordinates
        cam.getViewMatrix(); // Ensure view matrix is up-to-date
        cam.getProjectionMatrix(); // Ensure projection matrix is up-to-date

        // Transform the world coordinate to the viewport space
        const viewport = cam.viewport;
        const projectedPosition = BABYLON.Vector3.Project(
            position,
            BABYLON.Matrix.IdentityReadOnly,
            this.scene.getTransformMatrix(),
            viewport.toGlobal(
                this.scene.getEngine().getRenderWidth(),
                this.scene.getEngine().getRenderHeight()
            )
        );

        var tRay = this.scene.pickWithRay(new BABYLON.Ray(new BABYLON.Vector3(0, 0, 0), rotatedDir));

        // The screen coordinates
        const pointerX = projectedPosition.x;
        const pointerY = projectedPosition.y;

        return {
            sphere: sphereCoords,
            image: new BABYLON.Vector2(x, y),
            screen: new BABYLON.Vector2(pointerX, pointerY)
        };
    }
    public convertPositionToSphereCoordinates4(position: BABYLON.Vector3) {

        var cam = this.scene.activeCamera as any;
        var dir = position.subtract(this._imagePosition);

        var distance = dir.length();

        var m = new BABYLON.Matrix();
        var rotQuat = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), cam?._imageRotation);
        rotQuat.toRotationMatrix(m);
        dir = dir.normalize();
        var rotatedDir = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(dir.x, dir.y, dir.z), m);

        var sphereCoords = this.cartesianToSpherical(new BABYLON.Vector3(rotatedDir.x, rotatedDir.z, rotatedDir.y));

        var u = 1 - sphereCoords.x;
        var v = sphereCoords.y;
        var x = u * this._imageWidth;
        var y = v * this._imageHeight;
        x = Math.floor(x);
        y = Math.floor(y);

        return { sphere: sphereCoords, image: new BABYLON.Vector2(x, y) };
    }
    public isPositionOccluded(position: BABYLON.Vector3, scanPosition: BABYLON.Vector3) {

        var cam = this.scene.activeCamera as any;
        var dir = position.subtract(scanPosition);

        var distance = dir.length();
        
        var m = new BABYLON.Matrix();
        var rotQuat = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), cam?._imageRotation);
        rotQuat.toRotationMatrix(m);
        dir = dir.normalize();
        var rotatedDir = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(dir.x, dir.y, dir.z), m);

        var sphereCoords = this.cartesianToSpherical(new BABYLON.Vector3(rotatedDir.x, rotatedDir.z, rotatedDir.y));

        var u = 1 - sphereCoords.x;
        var v = sphereCoords.y;
        var x = u * this._imageWidth;
        var y = v * this._imageHeight;
        x = Math.floor(x);
        y = Math.floor(y);
        
        let allZeros = true;

        // Test every surrounding pixel.
        for (var xi = -1; xi <= 1; xi++) {
            for (var yi = -1; yi <= 1; yi++) {

                if (x + xi < 0)
                    x = this._imageWidth - 1;

                if (x + xi >= this._imageWidth)
                    x = 0;

                if (y + yi < 0)
                    y = this._imageHeight - 1;

                if (y + yi >= this._imageHeight)
                    y = 0;

                var depthMapDistance = this.getDepthFromPixel(x + xi, y + yi);

                if (allZeros && (depthMapDistance != 0))
                    allZeros = false;

                if ((distance - depthMapDistance) <= DepthMap.OCCLUSION_TOLERANCE)
                    return false;
            }
        }

        if (allZeros) // All pixels around the point are empty, therefore, no occlusion
            return false;
        else
            return true;
    }

    cartesianToSpherical(cartesian: BABYLON.Vector3) {
        var r = Math.sqrt(cartesian.x * cartesian.x + cartesian.y * cartesian.y + cartesian.z * cartesian.z);
        var theta = Math.atan2(cartesian.y, cartesian.x);
        var phi = Math.acos(cartesian.z / r);

        // Normalize theta to the range [0, 1]
        theta = (theta + Math.PI) / (2 * Math.PI);

        // Normalize phi to the range [0, 1]
        phi = phi / Math.PI;

        //console.log(theta + " " + phi);
        return new BABYLON.Vector2(theta, phi);
    }

    public convertLocalToScreenCoordinate(worldPosition: BABYLON.Vector3): { pointerX: number, pointerY: number } {
        const scene = this._scene;
        worldPosition = worldPosition.subtract(this._imagePosition);
        if (!scene.activeCamera) {
            throw new Error("Active camera not set in the scene");
        }

        // Ensure the camera's matrices are up-to-date
        scene.activeCamera.getViewMatrix();
        scene.activeCamera.getProjectionMatrix();

        // Transform the world coordinate to the viewport space
        const viewport = scene.activeCamera.viewport;
        const projectedPosition = BABYLON.Vector3.Project(
            worldPosition,
            BABYLON.Matrix.IdentityReadOnly,
            scene.getTransformMatrix(),
            viewport.toGlobal(
                scene.getEngine().getRenderWidth(),
                scene.getEngine().getRenderHeight()
            )
        );

        // Calculate screen coordinates
        const pointerX = projectedPosition.x;
        const pointerY = projectedPosition.y;

        // Return the calculated screen coordinates
        return { pointerX, pointerY };
    }

    public convertLocalToScreenCoordinate2(worldPosition: BABYLON.Vector3): { pointerX: number, pointerY: number } {
        var normPos = worldPosition.subtract(this._imagePosition).normalize();

        var pickTest = this.convertToLocalPickResult(0, 0);
        var rayPick = this._scene.pickWithRay(new BABYLON.Ray(pickTest.ray.origin, normPos));

        return null;
    }

    public convertScreenToLocalCoordinate(pointerX: number, pointerY: number) {

        var pickResult = this.convertToLocalPickResult(pointerX, pointerY);
        var uvPick = this._scene.pick(pointerX, pointerY);
        var pickDirection = new BABYLON.Vector3(uvPick.ray.direction.x, uvPick.ray.direction.z, uvPick.ray.direction.y); // To Do, this should rotate, and not just flip y/z
        var sphereicalCoords = this.cartesianToSpherical(pickDirection);

        var u = 1 - sphereicalCoords.x;
        var v = sphereicalCoords.y;

        var x = u * this._imageWidth;
        var y = v * this._imageHeight;

        x = Math.floor(x);
        y = Math.floor(y);

        var direction = pickResult.ray.direction;
        var distance = this.getDepthFromPixel(x, y);

        if (distance == 0) // Empty pixel on depth map, therefore, no world coordinate
            return null;

        var finalPos = direction.scale(distance).add(this._imagePosition);

        return finalPos;
    }

    private convertToLocalPickResult(pointerX: number, pointerY: number) {
        var cam = this.scene.activeCamera as any;

        var m = new BABYLON.Matrix();
        var rotQuat = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), cam?._imageRotation);
        rotQuat.toRotationMatrix(m);
        var pickingRay = this.scene.createPickingRay(pointerX, pointerY, m, this.scene.activeCamera);

        return this.scene.pickWithRay(pickingRay);
    }

    getDepthFromPixel(x: number, y: number) {
        var depth = this._depthMap[(/*this._imageHeight - */y) * this._imageWidth + x] / this._scaleFactor;
        return depth;
    }
  
    private readonly ZeroSequenceBitSize = 9;

    private downloadAndDecompressDepthMap(url:string, width:number, height:number,  signal? : AbortSignal) {
        return new Promise((resolve, reject) => {

            var worker = new Worker(new URL('./DepthMapWorker.ts', import.meta.url), { type: 'module' });
            
            worker.onmessage = function (e) {
                if (handleWorkerAuthRefreshMessage(worker, e)) {
                    return;
                }

                if (e.data.error) {
                    reject(new Error(e.data.error));
                } else {
                    resolve(e.data);
                }
            };

            worker.onerror = function (error) {
                reject(error);
            };

            worker.postMessage({ url, width, height, authContext: getCurrentWorkerAuthContext() });
        });
    }

    private async downloadAndDecompressDepthMap2(url: string, signal?: AbortSignal): Promise<Uint32Array> {
        const compressedArrayBuffer = await apiRequest<ArrayBuffer>({
            url,
            method: 'GET',
            bodyType: 'none',
            responseType: 'arrayBuffer',
            signal,
        });

        // Decompress using pako
        const decompressedArray = pako.inflate(new Uint8Array(compressedArrayBuffer));

        // Convert the decompressed array to a DataView
        const decompressedBuffer = decompressedArray.buffer;
        const dataView = new DataView(decompressedBuffer);

        // Decompress the data
        const depthMap = this.decompressDepthMap(dataView);

        // Convert to Uint32Array
        return new Uint32Array(depthMap);
    }

    private decompressDepthMap(dataView: DataView): number[] {
        const imageSize = this._imageWidth * this._imageHeight;
        const result = new Array(imageSize); // Pre-allocate
        let resultIndex = 0;
        let previousValue = 0;

        let position = 1; // byte 0 is the version
        while (position < dataView.byteLength) {
            let bitSizeIndicator = dataView.getUint8(position++);
            let count: number;

            if (bitSizeIndicator >= this.ZeroSequenceBitSize) {
                bitSizeIndicator -= this.ZeroSequenceBitSize;
                count = this.readCount(dataView, position, bitSizeIndicator);
                position += this.countBytes(bitSizeIndicator);

                // Add zeros
                result.fill(0, resultIndex, resultIndex + count);
                resultIndex += count;
            } else {
                count = this.readCount(dataView, position, bitSizeIndicator);
                position += this.countBytes(bitSizeIndicator);
                const bitSize = bitSizeIndicator % 3;

                for (let i = 0; i < count; i++) {
                    const delta = this.readValue(dataView, position, bitSize);
                    position += this.bitSizeBytes(bitSize);
                    previousValue += delta;
                    result[resultIndex++] = previousValue;
                }
            }
        }

        return result;
    }

    private readCount(dataView: DataView, position: number, bitSizeIndicator: number): number {
        const countBitSize = Math.floor(bitSizeIndicator / 3);
        if (countBitSize === 0) return dataView.getUint8(position);
        if (countBitSize === 1) return dataView.getUint16(position, true);
        return dataView.getUint32(position, true);
    }

    private readValue(dataView: DataView, position: number, bitSize: number): number {
        if (bitSize === 0) return dataView.getInt8(position);
        if (bitSize === 1) return dataView.getInt16(position, true);
        return dataView.getInt32(position, true);
    }

    private countBytes(bitSizeIndicator: number): number {
        const countBitSize = Math.floor(bitSizeIndicator / 3);
        if (countBitSize === 0) return 1;
        if (countBitSize === 1) return 2;
        return 4;
    }

    private bitSizeBytes(bitSize: number): number {
        if (bitSize === 0) return 1;
        if (bitSize === 1) return 2;
        return 4;
    }

}

export default DepthMap;